import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePreferences } from '@/store/preferences';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import AppleSignInButton from '@/components/auth/AppleSignInButton';
import { track, AnalyticsEvent } from '@/lib/analytics';


const Login = () => {
  const navigate = useNavigate();
  const { t, language } = usePreferences();
  const fr = language === 'fr';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !password) { setError(fr ? 'Veuillez remplir tous les champs.' : 'Please fill in all fields.'); return; }
    setSubmitting(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email: trimmed, password });
    setSubmitting(false);
    if (err) {
      if (/email not confirmed|not confirmed/i.test(err.message)) {
        setNeedsVerification(true);
        setError(fr ? 'Veuillez vérifier votre adresse email avant de vous connecter.' : 'Please verify your email address before logging in.');
        return;
      }
      setError(fr ? 'Email ou mot de passe incorrect.' : 'Invalid email or password.');
      return;
    }
    void track(AnalyticsEvent.LOGIN, { method: 'email' });
    toast.success(fr ? 'Bienvenue !' : 'Welcome back!');
    navigate('/', { replace: true });
  };

  const handleResendVerification = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    const { error: err } = await supabase.auth.resend({
      type: 'signup',
      email: trimmed,
      options: { emailRedirectTo: `${window.location.origin}/verify-email-confirm` },
    });
    if (err) toast.error(err.message);
    else toast.success(fr ? 'Email de vérification renvoyé.' : 'Verification email resent.');
  };

  const glassInput = 'rounded-xl border-border/60 bg-card/80 py-5 text-foreground placeholder:text-muted-foreground backdrop-blur-sm shadow-[inset_0_1px_2px_hsl(0_0%_0%/0.3)] focus:ring-primary/30 focus:border-primary/40 transition-all duration-200';

  return (
    <div className="relative mx-auto flex min-h-screen max-w-lg flex-col bg-background px-6 overflow-hidden">
      <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-[280px] w-[400px] rounded-full bg-primary/8 blur-[120px]" />
      <header className="relative z-10 flex items-center gap-3 py-4">
        <button onClick={() => navigate(-1)} className="text-foreground active:scale-90 transition-transform"><ArrowLeft className="h-5 w-5" /></button>
      </header>
      <div className="relative z-10 flex flex-1 flex-col justify-center pb-20">
        <h1 className="text-2xl font-extrabold uppercase tracking-widest text-foreground">{t.log_in}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t.welcome_back}</p>
        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-foreground">{t.email}</Label>
            <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} className={glassInput} autoComplete="email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-foreground">{t.password}</Label>
            <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} className={glassInput} autoComplete="current-password" />
            <div className="flex justify-end">
              <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs font-semibold text-primary hover:underline">
                {fr ? 'Mot de passe oublié ?' : 'Forgot Password?'}
              </button>
            </div>
          </div>
          {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{error}</p>}
          {needsVerification && (
            <button type="button" onClick={handleResendVerification} className="block w-full rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-[13px] font-semibold text-primary hover:bg-primary/15">
              {fr ? "Renvoyer l'email de vérification" : 'Resend verification email'}
            </button>
          )}
          <div className="pt-1">
            <div className="relative">
              <div className="absolute inset-0 rounded-xl bg-primary/25 blur-lg" />
              <Button type="submit" disabled={submitting} className="relative w-full rounded-xl bg-primary py-6 text-base font-bold text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 active:scale-[0.97] transition-transform disabled:opacity-60">
                {submitting ? (fr ? 'Connexion…' : 'Signing in…') : t.log_in}
              </Button>
            </div>
          </div>
        </form>
        <AppleSignInButton />

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t.no_account}{' '}
          <button onClick={() => navigate('/signup')} className="font-semibold text-primary hover:underline">{t.sign_up}</button>
        </p>
        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground">
          <button onClick={() => navigate('/privacy')} className="underline-offset-2 hover:text-foreground hover:underline">
            {fr ? 'Politique de confidentialité' : 'Privacy Policy'}
          </button>
          <span className="opacity-50">·</span>
          <button onClick={() => navigate('/terms')} className="underline-offset-2 hover:text-foreground hover:underline">
            {fr ? "Conditions d'Utilisation" : 'Terms of Use'}
          </button>
          <span className="opacity-50">·</span>
          <button onClick={() => navigate('/legal')} className="underline-offset-2 hover:text-foreground hover:underline">
            {fr ? 'Mentions légales' : 'Legal Notice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
